import React from "react";
import commonStyles from "pages/assistant/components/artifacts/components/fineco-portfolio/finecoPortfolio.common.module.scss";
import { useTranslation } from "react-i18next";
import { useComparisonLabels } from "../../compare/ComparisonLabelsContext";
import DateRangePicker from "shared/components/ui/date-range-picker";
// import SimulazioneAndamento from "./components/SimulazioneAndamento";
import SimulazionePerformance from "./components/SimulazionePerformance";
import SimulazioneGraficoVolatilita from "./components/SimulazioneGraficoVolatilita";
import RendimentiCumulatiPerHoldingPeriod from "./components/RendimentiCumulatiPerHoldingPeriod";
import DistribuzioneRendimenti1y from "./components/DistribuzioneRendimenti1y";
import SimulazioneAndamentoPortafoglio from "./components/SimulazioneAndamentoPortafoglio";
import SimulazionePerformanceCompare from "../../compare/components/SimulazionePerformanceCompare";
import AnalisiDrawdownBacktestTable from "./components/AnalisiDrawdownBacktestTable";
import AnalisiDrawdownBacktestAndamentoPortafoglio from "./components/AnalisiDrawdownBacktestAndamentoPortafoglio";
import SimulazioneRendimentiAnnoSolare from "./components/SimulazioneRendimentiAnnoSolare";
import RendimentiCumulatiPerHoldingPeriodTable from "./components/RendimentiCumulatiPerHoldingPeriodTable";
import ViolazioneMaxPerditaMaxGuadagno from "./components/ViolazioneMaxPerditaMaxGuadagno";
import StressDelPortafoglio from "./components/StressDelPortafolioSuIpotesiStoriche";
import AnalisiDrawdownPortfolioCompare from "../../compare/components/backtest/AnalisiDradownPortfolioCompare";
import AnalisiDrawdownChartsCompare from "../../compare/components/backtest/AnalisiDrawdownChartsCompare";
import SimulazioneRendimentiAnnoSolareCompare from "../../compare/components/backtest/SimulazioneRendimentiAnnoSolareCompare";
import SimulazioneGraficoVolatilitaCompare from "../../compare/components/backtest/SimulazioneGraficoVolatilitaCompare";
import ViolazioneMaxPerditaMaxGuadagnoCompare from "../../compare/components/backtest/ViolazioneMaxPerditaMaxGuadagnoCompare";
import StressDeiPortafogliScenariNegativiCompare from "../../compare/components/backtest/StressDeiPortafogliScenariNegativiCompare";
import colorsStore from "shared/store/colorsStore";
import { IconNote } from "shared/components/ui/IconNote";

type SectionChildrenProps = {
  reportData: any;
  reportData2?: any;
  startDate?: Date | null;
  endDate?: Date | null;
  onStartDateChange?: (date: Date | null) => void;
  onEndDateChange?: (date: Date | null) => void;
  backtestLoading?: boolean;
};

const BacktestContainer: React.FC<SectionChildrenProps> = ({
  reportData,
  reportData2,
  startDate,
  endDate,
  onStartDateChange,
  onEndDateChange,
  backtestLoading = false,
}) => {
  const colorsConfig = colorsStore((state) => state.colorsConfigStore);
  const { t } = useTranslation();
  const labels = useComparisonLabels();

  // Format title with date range when date picker is present
  const formatDateRangeTitle = (baseTitle: string, start?: Date | null, end?: Date | null) => {
    if (!start && !end) return baseTitle;

    const parts = [];
    if (start) {
      parts.push(`inizio: ${start.toLocaleDateString('it-IT')}`);
    }
    if (end) {
      parts.push(`fine: ${end.toLocaleDateString('it-IT')}`);
    }

    return `${baseTitle} (${parts.join(', ')})`;
  };

  if (!reportData) {
    return <></>;
  }

  return (
    <>
      {/* Date picker - shown in both single and comparison modes when callbacks are provided */}
      {onStartDateChange && onEndDateChange && (
        <div
          className={commonStyles.sectionContainer}
          style={{ marginBottom: 10 }}
        >
          <DateRangePicker
            startDate={startDate || null}
            endDate={endDate || null}
            onStartDateChange={onStartDateChange}
            onEndDateChange={onEndDateChange}
            className={commonStyles.dateRangePicker}
            disabled={backtestLoading}
            labels={{
              startDate: t("assistant.dateRange.startDate"),
              endDate: t("assistant.dateRange.endDate"),
              selectStartDate: t("assistant.dateRange.selectStartDate"),
              selectEndDate: t("assistant.dateRange.selectEndDate")
            }}
          />
        </div>
      )}

      {/* Certificate backtest warning */}
      {reportData?.analisi_backtest?.backtest_certificate_warning && (
        <div
          className={commonStyles.sectionContainer}
          style={{ marginBottom: 10 }}
        >
          <IconNote
            type="attention"
            title="Attenzione"
            body={reportData.analisi_backtest.backtest_certificate_warning}
          />
        </div>
      )}

      {/* Policy backtest warning */}
      {reportData?.analisi_backtest?.backtest_policy_warning && (
        <div
          className={commonStyles.sectionContainer}
          style={{ marginBottom: 10 }}
        >
          <IconNote
            type="attention"
            title="Attenzione"
            body={reportData.analisi_backtest.backtest_policy_warning}
          />
        </div>
      )}

      {!reportData2 && (
        <div
          className={commonStyles.sectionContainer}
          style={{ marginBottom: 10 }}
        >
          <div style={{ position: 'relative' }}>
            {backtestLoading && (
                <div
                  style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    paddingTop: '128px',
                    backgroundColor: 'rgba(255, 255, 255, 0.9)',
                    display: 'flex',
                    justifyContent: 'center',
                    zIndex: 1,
                    borderRadius: '4px',
                  }}
                >
                  <div style={{ textAlign: 'center', color: '#666' }}>
                    <div
                      style={{
                        border: '3px solid #f3f3f3',
                        borderTop: '3px solid #007bff',
                        borderRadius: '50%',
                        width: '40px',
                        height: '40px',
                        animation: 'spin 1s linear infinite',
                        margin: '0 auto 10px',
                      }}
                    />
                    {t("portfolio.loadingBacktestData")}
                  </div>
                </div>
            )}

            <SimulazioneAndamentoPortafoglio
              data={reportData}
              title={formatDateRangeTitle(t("portfolio.simulazioneAndamentoPortfolio"), startDate, endDate)}
            />
            <SimulazionePerformance
              data={reportData}
              title={formatDateRangeTitle(t("portfolio.simulazione_performance_title"), startDate, endDate)}
            />
            <AnalisiDrawdownBacktestTable
              data={reportData}
              title={formatDateRangeTitle(t("portfolio.analisiDrawdown"), startDate, endDate)}
            />
            <AnalisiDrawdownBacktestAndamentoPortafoglio data={reportData} />
            <SimulazioneRendimentiAnnoSolare
              data={reportData}
              title={formatDateRangeTitle(t("portfolio.simulazioneRendimentiAnnoSolare"), startDate, endDate)}
            />
          </div>
          <SimulazioneGraficoVolatilita data={reportData} />
          <RendimentiCumulatiPerHoldingPeriod
            data={reportData}
            title={t("portfolio.rendimentiCumulatiDelPortafoglio")}
            mainColor={colorsConfig.FinecoBluePrimary}
            mainColorFill={colorsConfig.finecoBlueLight}
          />
          <RendimentiCumulatiPerHoldingPeriodTable data={reportData} />
          <DistribuzioneRendimenti1y data={reportData} />
          <ViolazioneMaxPerditaMaxGuadagno
            data={reportData}
            title={t("portfolio.violazioniMaxPerditaMaxGuadagno")}
          />
          <StressDelPortafoglio data={reportData} mood="negative" />
          <StressDelPortafoglio data={reportData} mood="positive" />
        </div>
      )}
      {reportData2 && (
        <div
          className={commonStyles.sectionContainer}
          style={{ marginBottom: 10, position: 'relative' }}
        >
          {backtestLoading && (
            <div
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                paddingTop: '128px',
                backgroundColor: 'rgba(255, 255, 255, 0.9)',
                display: 'flex',
                justifyContent: 'center',
                zIndex: 1,
                borderRadius: '4px',
              }}
            >
              <div style={{ textAlign: 'center', color: '#666' }}>
                <div
                  style={{
                    border: '3px solid #f3f3f3',
                    borderTop: '3px solid #007bff',
                    borderRadius: '50%',
                    width: '40px',
                    height: '40px',
                    animation: 'spin 1s linear infinite',
                    margin: '0 auto 10px',
                  }}
                />
                {t("portfolio.loadingBacktestData")}
              </div>
            </div>
          )}

          <SimulazioneAndamentoPortafoglio
            data={reportData}
            data2={reportData2}
            title={formatDateRangeTitle(t("portfolio.simulazioneAndamentoPortfolioCompare"), startDate, endDate)}
          />
          <SimulazionePerformanceCompare
            data={reportData}
            data2={reportData2}
            title={formatDateRangeTitle(t("portfolio.simulazione_performance_titleCompare"), startDate, endDate)}
          />
          <div className="force-page-break"></div>
          <AnalisiDrawdownPortfolioCompare
            data={reportData}
            data2={reportData2}
            title={formatDateRangeTitle(t("portfolio.analisiDrawdown"), startDate, endDate)}
          />
          <AnalisiDrawdownChartsCompare data={reportData} data2={reportData2} />
          <div className="force-page-break"></div>
          <SimulazioneRendimentiAnnoSolareCompare
            data={reportData}
            data2={reportData2}
            title={formatDateRangeTitle(t("portfolio.simulazioneRendimentiAnnoSolareCompare"), startDate, endDate)}
          />
          <SimulazioneGraficoVolatilitaCompare
            data={reportData}
            data2={reportData2}
          />
          <div className="force-page-break"></div>
          <RendimentiCumulatiPerHoldingPeriod
            data={reportData}
            title={`Rendimenti cumulati del ${labels.portfolio1.toLowerCase()} per holding period`}
            mainColor={colorsConfig.portfolio1}
            mainColorFill={"#CFD7E4"}
          />

          <RendimentiCumulatiPerHoldingPeriodTable
            data={reportData}
            title={t("portfolio.orizzonteTemporale")}
          />
          <div className="force-page-break"></div>
          <RendimentiCumulatiPerHoldingPeriod
            data={reportData2}
            title={`Rendimenti cumulati del ${labels.portfolio2.toLowerCase()} per holding period`}
            mainColor={colorsConfig.portfolio2}
            mainColorFill={"#D6ECF0"}
          />
          <RendimentiCumulatiPerHoldingPeriodTable
            data={reportData2}
            title={t("portfolio.orizzonteTemporale")}
          />
          <div className="force-page-break"></div>
          <DistribuzioneRendimenti1y
            data={reportData}
            colorBarChart={colorsConfig.FinecoBlueDark}
            isCompareTitle={t("portfolio.distribuzioneRendimenti1y")}
            isCompareSubtitle={labels.portfolio1}
          />
          <DistribuzioneRendimenti1y
            data={reportData2}
            colorBarChart={colorsConfig.portfolio2}
            isCompareSubtitle={labels.portfolio2}
          />
          <div className="force-page-break"></div>
          <ViolazioneMaxPerditaMaxGuadagnoCompare
            data={reportData}
            data2={reportData2}
            title={t("portfolio.violazioniMaxPerditaMaxGuadagno")}
          />
          <div className="force-page-break"></div>
          <StressDeiPortafogliScenariNegativiCompare
            reportData={reportData}
            reportData2={reportData2}
          />
        </div>
      )}
    </>
  );
};
export default React.memo(
  BacktestContainer,
  (prevProps: SectionChildrenProps, nextProps: SectionChildrenProps) => {
    // Helper to compare dates properly
    const datesEqual = (d1?: Date | null, d2?: Date | null) => {
      if (d1 === d2) return true;
      if (!d1 || !d2) return false;
      return d1.getTime() === d2.getTime();
    };

    return (
      prevProps.reportData === nextProps.reportData &&
      prevProps.reportData2 === nextProps.reportData2 &&
      datesEqual(prevProps.startDate, nextProps.startDate) &&
      datesEqual(prevProps.endDate, nextProps.endDate) &&
      prevProps.backtestLoading === nextProps.backtestLoading
    );
  },
);